
import React, { useEffect } from 'react';
import { useSafeAreaPadding } from '@/hooks/useSafeAreaPadding';
import { usePlatformDetection } from '@/utils/platform-utils';
import { useKeyboardSafeArea } from '@/hooks/useKeyboardSafeArea';

interface SafeAreaProviderProps {
  children: React.ReactNode;
}

export const SafeAreaProvider: React.FC<SafeAreaProviderProps> = ({ children }) => {
  const { platformType } = usePlatformDetection();
  const { 
    paddingTop, 
    paddingBottom, 
    paddingLeft, 
    paddingRight, 
    containerClasses 
  } = useSafeAreaPadding();
  
  // Monitor keyboard state for iOS
  const { isKeyboardOpen, adjustedPaddingBottom } = useKeyboardSafeArea();

  useEffect(() => {
    // Always set CSS custom properties for all platforms
    const root = document.documentElement;
    root.style.setProperty('--safe-padding-top', paddingTop);
    
    // Use keyboard-adjusted padding for iOS, or regular padding for other platforms
    const finalPaddingBottom = platformType === 'ios' && isKeyboardOpen 
      ? adjustedPaddingBottom 
      : paddingBottom;
    
    root.style.setProperty('--safe-padding-bottom', finalPaddingBottom);
    root.style.setProperty('--safe-padding-left', paddingLeft);
    root.style.setProperty('--safe-padding-right', paddingRight);

    console.log('Safe Area Padding Applied for', platformType, {
      paddingTop,
      paddingBottom: finalPaddingBottom,
      paddingLeft,
      paddingRight,
      containerClasses,
      isKeyboardOpen
    });
  }, [paddingTop, paddingBottom, paddingLeft, paddingRight, containerClasses, platformType, isKeyboardOpen, adjustedPaddingBottom]);

  useEffect(() => {
    // Apply platform-specific classes to body
    const body = document.body;
    
    // Remove all existing safe area classes
    body.classList.remove(
      'ios-safe-container', 
      'ios-native-safe-container',
      'android-safe-container', 
      'web-safe-container'
    );
    
    // Add current platform class
    body.classList.add(containerClasses);

    console.log('Applied platform class:', containerClasses);

    return () => {
      body.classList.remove(containerClasses);
    };
  }, [containerClasses]);

  return <>{children}</>;
};
